"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import Image from "next/image";
import { motion } from "framer-motion";
import { parseJsonBody } from "@/lib/general.utils";
import useGeneralStore from "@/store/General/useGeneralStore";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";
import { PlayArrow } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const ServiceDetailComponent = () => {
  const { t, i18n } = useTranslation("common");
  const router = useRouter();
  const { slug } = router.query;
  const isRtl = i18n.language === "ar";
  const [videoOpen, setVideoOpen] = useState(false);
  const { ServicesComponent: servicesData } = useGeneralStore((state) => ({
    ServicesComponent: state.ServicesComponent,
  }));
  const servicesJson = parseJsonBody(servicesData?.jsonBody);
  const services = servicesJson?.services || [];
  const service = services.find((s: any) => s.slug === slug);

  useEffect(() => {
    if (services.length > 0 && !service && slug) {
      router.push("/services");
    }
  }, [services, service, slug, router]);

  if (!service) {
    return (
      <div className=" py-[4rem] px-[2rem] flex items-center justify-center">
        <p className="text-gray-500">{t("services.notFound")}</p>
      </div>
    );
  }

  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const videoId = service.videoUrl ? getYouTubeId(service.videoUrl) : null;
  const hasVideo = !!videoId;
  const hasImage = !!service.imgUrl;
  const youtubeThumbnail = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null;

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      lang={i18n.language}
      className="py-[4rem] px-[2rem]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[3rem] items-start">
          {/* Left Side - Image/Video */}
          <motion.div
            initial={{ opacity: 0, x: isRtl ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative w-full rounded-lg overflow-hidden"
          >
            {hasVideo ? (
              <div className="relative w-full aspect-video rounded-lg overflow-hidden cursor-pointer group">
                {hasImage ? (
                  <>
                    <Image
                      src={service.imgUrl || ""}
                      alt={service.title}
                      fill
                      className="object-cover"
                    />
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors"
                      onClick={() => setVideoOpen(true)}
                    >
                      <div className="w-[5rem] h-[5rem] bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <PlayArrow className="text-primary text-[3rem] ml-[0.3rem]" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="relative w-full aspect-video rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => setVideoOpen(true)}
                  >
                    {youtubeThumbnail && (
                      <Image
                        src={youtubeThumbnail || ""}
                        alt={service.title}
                        fill
                        className="object-cover"
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                      <div className="w-[5rem] h-[5rem] bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <PlayArrow className="text-primary text-[3rem] ml-[0.3rem]" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : hasImage ? (
              <div className="relative w-full aspect-video rounded-lg overflow-hidden">
                <Image
                  src={service.imgUrl || ""}
                  alt={service.title}
                  fill
                  className="object-cover"
                />
              </div>
            ) : null}
          </motion.div>

          {service.gallery?.length > 0 && (
            <div className="grid grid-cols-2 gap-4 lg:col-start-1 lg:row-start-2">
              {service.gallery.map((src: string) => (
                <div
                  key={src}
                  className="relative aspect-[4/3] overflow-hidden rounded-lg"
                >
                  <Image
                    src={src}
                    alt={service.title}
                    fill
                    className="object-cover"
                  />
                </div>
              ))}
            </div>
          )}
          {/* Right Side - Content */}
          <motion.div
            initial={{ opacity: 0, x: isRtl ? -50 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col gap-[2rem] lg:col-start-2 lg:row-start-1 lg:row-span-2"
          >
            <h1 className="text-[2.5rem] md:text-[3rem] font-bold text-primary">
              {service.title}
            </h1>

            {service.description && (
              <p className="text-gray-700 text-[1rem] md:text-[1.1rem] leading-relaxed">
                {service.description}
              </p>
            )}

            {/* Links */}
            {service.links && service.links.length > 0 && (
              <div className="flex flex-col gap-[2rem] lg:col-start-2 lg:row-start-1 lg:row-span-2">
                {service.linksSectionTitle && (
                  <h2 className="text-primary font-bold text-[1.1rem] md:text-[1.2rem] mb-[0.5rem]">
                    {service.linksSectionTitle}
                  </h2>
                )}
                <div className="flex flex-col gap-[0.5rem]">
                  {service.links.map((link: any, index: number) =>
                    !link.description ? (
                      <div
                        key={link.id || index}
                        className="rounded-xl border border-primary/10 bg-primary/5 p-5 text-primary leading-relaxed"
                      >
                        {link.title}
                      </div>
                    ) : (
                      <Accordion
                        key={link.id || index}
                        sx={{
                          borderRadius: "0.7rem",
                          border: "1px solid rgba(0, 0, 0, 0.12)",
                          boxShadow: "none",
                          "&:before": {
                            display: "none",
                          },
                          "&.Mui-expanded": {
                            margin: "0.5rem 0",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon className="text-primary" />
                          }
                          sx={{
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                            borderRadius: "0.7rem",
                            "&.Mui-expanded": {
                              borderRadius: "0.7rem 0.7rem 0 0",
                            },
                          }}
                        >
                          <div className="flex items-start gap-[0.75rem] w-full">
                            <h3 className="text-primary font-semibold text-[1.1rem] md:text-[1.2rem]">
                              {link.title}
                            </h3>
                          </div>
                        </AccordionSummary>
                        {link.description && (
                          <AccordionDetails>
                            <p className="text-gray-700 text-[0.95rem] md:text-[1rem] leading-relaxed">
                              {link.description}
                            </p>
                          </AccordionDetails>
                        )}
                      </Accordion>
                    ),
                  )}
                </div>
              </div>
            )}

            {/* Achievements */}
            {service.achievements && service.achievements.length > 0 && (
              <div className="mt-[2rem]">
                {service.achievementsSectionTitle && (
                  <h2 className="text-[1.5rem] md:text-[2rem] font-bold text-primary mb-[1.5rem]">
                    {service.achievementsSectionTitle}
                  </h2>
                )}
                <div className="flex flex-col gap-[0.5rem]">
                  {service.achievements.map(
                    (achievement: any, index: number) => (
                      <div key={index} className="flex flex-col gap-[0.5rem]">
                        <div
                          key={achievement.id || index}
                          className="rounded-[0.7rem] border  p-[1rem] text-[1.2rem] md:text-[1.5rem]"
                        >
                          <div className="flex items-center justify-between gap-[1.5rem] w-full">
                            <div className="bg-primary text-white p-[0.4rem] px-4 rounded-lg flex-shrink-0">
                              <div className="text-center flex items-center justify-center gap-[0.5rem] ">
                                <span>{achievement.value}</span>
                              </div>
                            </div>
                            <div>
                              <p>{achievement.title}</p>
                            </div>
                          </div>
                        </div>
                        {index < service.achievements.length - 1 && (
                          <Divider className="my-[1rem] border-primary" />
                        )}
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <Dialog
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxWidth: "90vw",
            width: "100%",
            m: 2,
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            width: "100%",
            height: "90vh",
            overflow: "hidden",
          }}
        >
          <IconButton
            onClick={() => setVideoOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "white",
              zIndex: 1,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {videoId && (
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={service.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceDetailComponent;
